import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { CreatableGroups } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { FontWeight } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { GroupTypeApi } from 'api-typings/groups/groups';

@Component({
  standalone: false,
  selector: 'app-create-new-group-button',
  template: `
    <fl-button
      i18n="Create a group button"
      class="CreateButton"
      flTrackingSection="CreateGroupButtonComponent"
      flTrackingLabel="CreateGroupButton"
      [color]="disabled ? ButtonColor.DEFAULT : buttonColor"
      [display]="'flex'"
      [disabled]="disabled"
      [busy]="loading"
      [size]="alwaysShowFullButton ? ButtonSize.SMALL : ButtonSize.MINI"
      [sizeTablet]="ButtonSize.SMALL"
      (click)="openModal()"
    >
      <fl-icon
        class="CreateButton-icon mr-xxxsmall"
        [color]="disabled ? IconColor.INHERIT : IconColor.LIGHT"
        [class.Disabled]="disabled"
        [name]="'ui-plus'"
        [size]="IconSize.SMALL"
        [useIconFont]="true"
        [fillIconFont]="true"
        [weight]="FontWeight.BOLD"
      ></fl-icon>
      <p
        class="text-size-inherit font-weight-inherit"
        [class.text-color-inherit]="disabled"
        [class.text-light]="!disabled"
        [class.Disabled]="disabled"
      >
        Create
      </p>
      <p
        class="text-size-inherit CreateButton-extra font-weight-inherit"
        [class.text-color-inherit]="disabled"
        [class.text-light]="!disabled"
        [class.Disabled]="disabled"
        [class.desktop:show]="alwaysShowFullButton ? undefined : true"
      >
        Group
      </p>
    </fl-button>
  `,
  styleUrls: ['./create-new-group-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNewGroupButtonComponent {
  private modalService = inject(ModalService);
  private toastAlertService = inject(ToastAlertService);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;

  /** If set to true, the + icon and extra text will always be shown, no matter the screen size. */
  @Input() alwaysShowFullButton = false;
  @Input() buttonColor: ButtonColor = ButtonColor.SECONDARY;
  @Input()
  loading?: boolean = false;
  @Input() disabled = false;
  @Input() disableModal = false;
  @Input() defaultGroupType: CreatableGroups = GroupTypeApi.COMMUNITY;
  @Input() groupTypes: readonly CreatableGroups[] = [GroupTypeApi.COMMUNITY];

  openModal(): void {
    if (this.disableModal) {
      return;
    }

    this.modalService
      .open('CreateNewGroupModal', {
        mobileFullscreen: true,
        size: ModalSize.SMALL,
        closeable: true,
        inputs: {
          defaultGroupType: this.defaultGroupType,
          groupTypes: this.groupTypes,
        },
      })
      .afterClosed()
      .then(showToast => {
        if (showToast) {
          this.toastAlertService.open({
            type: ToastAlertType.SUCCESS,
            message: $localize`We'll review your request and be in touch soon!`,
          });
        }
      });
  }
}
